import { Query } from '@apollo/client/react/components';
import { getConfigSetting } from '@components/configuration/configuration';
import EditJob from '@components/jobs/EditJob';
import { ConfigName } from '@constants/ConfigName';
import Grid from '@material-ui/core/Grid';
import { MuiThemeProvider } from '@material-ui/core/styles';
import * as Sentry from '@sentry/browser';
import { BroadcastChannel } from 'broadcast-channel';
import React, { Component, lazy, Suspense } from 'react';
import IdleTimer from 'react-idle-timer';
import { Route, Router, Switch } from 'react-router-dom';

import './App.css';
import { FilterStateContextProvider } from './context/FilterStateContext';

import ClientDashboard from './_components/ClientDashboard';
import InactivityModal from './_components/InactivityModal';
import Loader from './_components/Loader';
import NavigationBar from './_components/navbar/NavigationBar';
import { hasPermission } from './_components/permissions/CheckPermission';
import { PrivateRoute } from './_components/routes/PrivateRoute';
import TnSnackBar from './_components/snackbar/TnSnackBar';
import { history } from './_helpers/history';
import { hasMarketplaceAccessInAnyWorkspace, isVendorUser } from './_helpers/MarketplaceAccessHelper';
import auth from './_services/auth/Auth';
import { GET_LOGGEDIN } from './_services/client-quieries/clienttypes';
import { ThemeContext } from './store/ThemeContext';
import Callback from './views/Callback';

import { ClientData } from './index';

const channel = new BroadcastChannel('talentnow-marketplace-' + process.env.ENV || 'prod');

let idleTimer: any = null;

function setTimeoutModalVisibility() {
  localStorage.setItem('hideTimeoutModal', 'false');
}

channel.onmessage = (msg: string) => {
  if (msg === 'Stop timer') {
    idleTimer && idleTimer.pause();
  } else if (msg === 'Detached Logout') {
    setTimeoutModalVisibility();
    history.push({ pathname: '/login', state: { timedOut: 'true' } });
  }
};

interface IProps {}

interface IState {
  [key: string]: any;
}

const Jobs = lazy(() => import('../src/_components/jobs/Jobs'));
const JobDetails = lazy(() => import('../src/_components/jobs/JobDetails'));
const LinkedJobDetails = lazy(() => import('../src/_components/jobs/LinkedJobDetails'));
const CreateJob = lazy(() => import('../src/_components/jobs/CreateJob'));
const CandidateForm = lazy(() => import('../src/views/candidate/CandidateForm'));
const Marketplace = lazy(() => import('../src/views/marketplace/Marketplace'));
const CandidateView = lazy(() => import('./views/candidate-details/CandidateMainPage'));
const Redirect = lazy(() => import('./views/Redirect'));
const PrivacyPolicy = lazy(() => import('./views/login/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./views/login/TermsOfUse'));
const LoginScreen = lazy(() => import('./views/login/LoginScreen'));
const SystemSettings = lazy(() => import('./views/adminSettings/SystemSettings'));
const VendorDetails = lazy(() => import('./views/vendor/vendor-details/VendorDetails'));
const VendorListContainer = lazy(() => import('./views/vendor/VendorListContainer'));
const VendorOnboarding = lazy(() => import('./views/vendor/VendorOnboarding'));
const AddVendorDetails = lazy(() => import('./views/add-vendor-details/AddVendorDetailsMainPage'));
const WorkspaceRedirect = lazy(() => import('./views/WorkspaceRedirect'));

class App extends Component<IProps, IState> {
  static contextType = ThemeContext;

  constructor(props: IProps) {
    super(props);
    this.state = {
      hasCreateJob: false,
      hasSubmitCandidate: false,
      hasVendorAccess: false,
      hasVendorOnboardAccess: false,
      hasVendorView: false,
      hasAdminSettings: false,
      hasSuperAdminSettings: false,
      isVendorManagementEnabled: false,
      isJobsV2Enabled: false,
      isPrescreenEnabled: false,
      hasJobsAccess: !isVendorUser(),
      time: parseInt(process.env.UI_SESSION_TIMEOUT || '1800000'),
      open: false,
      remainingTime: 0,
      heading: 'Inactivity Timer',
      showMessage: false,
      message: '',
      variant: 'info',
    };
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.onIdle = this.onIdle.bind(this);
    this.stayHere = this.stayHere.bind(this);
    this.logout = this.logout.bind(this);
    this.onAction = this.onAction.bind(this);
    this.handleOnClose = this.handleOnClose.bind(this);
    this.setPermissions();
  }

  getConfigs = () => {
    getConfigSetting(ConfigName.VENDOR_MANAGEMENT).then((result) => {
      this.setState({ isVendorManagementEnabled: result });
    });
    getConfigSetting(ConfigName.TALENTHUB_JOBS_V2)
      .then((result) => {
        this.setState({ isJobsV2Enabled: result });
      })
      .catch((error) => {
        console.error('Error checking if V2 Jobs is enabled:', error);
      });
    getConfigSetting(ConfigName.PRESCREEN)
      .then((result) => {
        this.setState({ isPrescreenEnabled: result });
      })
      .catch((error) => {
        console.error('Error checking if Prescreen is enabled:', error);
      });
  };

  setJobsAccess = async () => {
    this.setState({ hasJobsAccess: await hasMarketplaceAccessInAnyWorkspace() });
  };

  componentDidMount() {
    const sessionExpiry = parseInt(localStorage.getItem('st') || '0');
    if (new Date().getTime() > sessionExpiry && sessionExpiry != 0) {
      history.push('/login');
    }
    localStorage.removeItem('st');
    this.getConfigs();
    this.setJobsAccess();
  }

  componentDidCatch(err: any, info: any) {
    Sentry.captureException(err);
  }

  handleVisibilityChange() {
    if (document.visibilityState == 'visible') {
      if (!(window.location.href.indexOf('login') > -1)) {
        channel.postMessage('Stop timer');
      }
      idleTimer && idleTimer.resume();
      const sessionExpiry = parseInt(localStorage.getItem('st') || '0');
      if (new Date().getTime() > sessionExpiry && sessionExpiry != 0) {
        setTimeoutModalVisibility();
        history.push({ pathname: '/login', state: { timedOut: 'true' } });
      }
      localStorage.removeItem('st');
    }
  }

  onIdle(e: any) {
    if (this.state.time == parseInt(process.env.UI_SESSION_TIMEOUT || '1800000')) {
      this.openModal();
    } else {
      channel.postMessage('Detached Logout');
      setTimeoutModalVisibility();
      history.push({ pathname: '/login', state: { timedOut: 'true' } });
      this.setState({
        showMessage: true,
        message: 'Your session has been timed out',
        variant: 'info',
      });
      setTimeout(this.closeSnackBar, 3000);
    }

    this.setState(
      {
        time: parseInt(process.env.UI_TIMEOUT_BUFFER || '60000'),
        remainingTime: idleTimer.getRemainingTime(),
      },
      () => {
        idleTimer.reset();
      },
    );
  }

  onAction() {
    localStorage.removeItem('st');
  }

  openModal = () => {
    this.setState({ open: true });
  };

  handleOnClose = () => {
    this.setState({ open: false });
  };

  closeSnackBar = () => {
    this.setState({ showMessage: false });
  };

  stayHere = () => {
    if (this.state.open) {
      this.handleOnClose();

      auth.silentAuth();
      this.setState({ time: parseInt(process.env.UI_SESSION_TIMEOUT || '1800000') }, () => {
        idleTimer.reset();
        this.setState({
          showMessage: true,
          message: 'Thank you for confirming',
          variant: 'info',
        });
        setTimeout(this.closeSnackBar, 3000);
      });
    }
  };

  logout = () => {
    this.handleOnClose();
    history.push('/login');
  };

  handleClose = () => {
    this.setState({
      showMessage: false,
    });
  };

  setPermissions = async () => {
    this.setState({
      hasCreateJob: await hasPermission('job:create'),
      hasSubmitCandidate: await hasPermission('candidate:submit'),
      hasAdminSettings: await hasPermission('system_settings'),
      hasSuperAdminSettings: await hasPermission('super_admin_settings'),
      hasVendorAccess: await hasPermission('vendor:access'),
      hasVendorOnboardAccess: await hasPermission('vendor:onboard'),
      hasVendorView: await hasPermission('vendor:view'),
    });
  };

  render():
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | string
    | number
    | {}
    | React.ReactNodeArray
    | React.ReactPortal
    | boolean
    | null
    | undefined {
    return (
      <MuiThemeProvider theme={this.context.currentTheme.theme}>
        <Router history={history}>
          <Grid container style={{ minHeight: '100vh' }}>
            <Grid item={true} xs={12}>
              <Query<ClientData> query={GET_LOGGEDIN}>
                {({ data }) => {
                  if (data && data.isLoggedIn) {
                    //@ts-ignore
                    // window.showChatBox(data.loggedInUserFirstname + " " + data.loggedInUserLastname);
                    document.addEventListener('visibilitychange', this.handleVisibilityChange, false);

                    return (
                      <React.Fragment>
                        <NavigationBar
                          role={data.loggedInUserRole}
                          userName={data.loggedInUserFirstname + ' ' + data.loggedInUserLastname}
                          imageId={data.loggedInUserAuth0Id}
                        />
                        <IdleTimer
                          ref={(ref) => {
                            idleTimer = ref;
                          }}
                          element={document}
                          onIdle={this.onIdle}
                          debounce={250}
                          onAction={this.onAction}
                          timeout={this.state.time}
                        />
                        <InactivityModal
                          open={this.state.open}
                          stayHere={this.stayHere}
                          logout={this.logout}
                          heading={this.state.heading}
                          handleOnClose={this.handleOnClose}
                        />
                      </React.Fragment>
                    );
                  } else {
                    //@ts-ignoregit push --set-upstream origin VMS-5369
                    //  window.hideChatBox()
                    return null;
                  }
                }}
              </Query>
              <Suspense fallback={<Loader />}>
                <Switch>
                  <PrivateRoute exact path="/" render={(props: any) => <ClientDashboard {...props} />} />
                  {this.state.hasJobsAccess && [
                    <PrivateRoute key="0" exact path="/jobs" render={(props: any) => <Jobs {...props} />} />,
                    <PrivateRoute
                      key="1"
                      exact
                      path="/jobs/:jobId/details"
                      render={(props: any) => <JobDetails {...props} />}
                    />,
                  ]}
                  {this.state.hasJobsAccess && this.state.isJobsV2Enabled && (
                    <PrivateRoute
                      exact
                      path="/jobs/:jobId/linked-jobs/:clientJobId"
                      render={(props: any) => <LinkedJobDetails {...props} />}
                    />
                  )}
                  {this.state.hasCreateJob && (
                    <PrivateRoute exact path="/createJob" render={(props: any) => <CreateJob {...props} />} />
                  )}
                  {this.state.hasSubmitCandidate && (
                    <PrivateRoute
                      exact
                      path="/submitCandidate"
                      render={(props: any) => <CandidateForm {...props} mode={'create'} />}
                    />
                  )}
                  {this.state.hasSubmitCandidate && [
                    <PrivateRoute
                      key="0"
                      exact
                      path={'/editCandidate/:id'}
                      render={(props: any) => <CandidateForm {...props} mode={'edit'} />}
                    />,
                  ]}
                  <PrivateRoute exact path="/editJob/:id" render={(props: any) => <EditJob {...props} />} />
                  <PrivateRoute
                    exact
                    path="/marketplace"
                    render={(props: any) => (
                      <FilterStateContextProvider>
                        <Marketplace {...props} />
                      </FilterStateContextProvider>
                    )}
                  />
                  <PrivateRoute
                    exact
                    path="/candidates"
                    render={(props: any) => (
                      <FilterStateContextProvider>
                        <Marketplace {...props} />
                      </FilterStateContextProvider>
                    )}
                  />
                  <PrivateRoute
                    exact
                    path={'/candidates/:id/profile'}
                    render={(props: any) => <CandidateView {...props} tabItem={'profile'} />}
                  />
                  <PrivateRoute
                    exact
                    path={'/candidates/:id/activities'}
                    render={(props: any) => <CandidateView {...props} tabItem={'activities'} />}
                  />
                  <PrivateRoute
                    exact
                    path={'/candidates/:id/currentOffers'}
                    render={(props: any) => <CandidateView {...props} tabItem={'current-offers'} />}
                  />
                  {this.state.isPrescreenEnabled && (
                    <PrivateRoute
                      exact
                      path={'/candidates/:id/prescreens'}
                      render={(props: any) => <CandidateView {...props} tabItem={'prescreens'} />}
                    />
                  )}
                  {this.state.hasAdminSettings && [
                    <PrivateRoute
                      key="0"
                      exact
                      path={'/systemSettings/user'}
                      render={(props: any) => <SystemSettings {...props} tabItem={'user'} />}
                    />,
                    <PrivateRoute
                      key="1"
                      exact
                      path={'/systemSettings/candidates'}
                      render={(props: any) => <SystemSettings {...props} tabItem={'candidates'} />}
                    />,
                    <PrivateRoute
                      key="2"
                      exact
                      path={'/systemSettings/superAdminSettings'}
                      render={(props: any) => <SystemSettings {...props} tabItem={'superAdminSettings'} />}
                    />,
                    <PrivateRoute
                      key="3"
                      exact
                      path={'/systemSettings/candidates/contactManagement'}
                      render={(props: any) => <SystemSettings {...props} tabItem={'candidates'} />}
                    />,
                    <PrivateRoute
                      key="4"
                      exact
                      path={'/systemSettings/user/accounts'}
                      render={(props: any) => <SystemSettings {...props} tabItem={'superAdminSettings'} />}
                    />,
                    <PrivateRoute
                      key="5"
                      exact
                      path={'/systemSettings/superAdminSettings/configs'}
                      render={(props: any) => <SystemSettings {...props} tabItem={'superAdminSettings'} />}
                    />,
                  ]}
                  <Route exact path="/callback" render={(props: any) => <Callback {...props} />} />
                  <Route exact path="/redirect" render={(props: any) => <Redirect {...props} />} />
                  <Route
                    exact
                    path={['/login', '/change-password']}
                    render={(props: any) => <LoginScreen {...props} />}
                  />
                  <Route exact path={'/terms-of-use'} render={(props: any) => <TermsOfUse {...props} />} />
                  <Route exact path={'/privacy-policy'} render={(props: any) => <PrivacyPolicy {...props} />} />
                  {this.state.isVendorManagementEnabled && (
                    <Route exact path={'/vendorOnboarding'} render={(props: any) => <VendorOnboarding {...props} />} />
                  )}
                  <Route
                    exact
                    path={'/addVendorDetails/:vendorId/:verificationCode'}
                    render={(props: any) => <AddVendorDetails {...props} />}
                  />
                  {this.state.hasVendorAccess && this.state.isVendorManagementEnabled && (
                    <PrivateRoute exact path={'/vendors'} render={(props: any) => <VendorListContainer {...props} />} />
                  )}
                  {this.state.hasVendorAccess &&
                    this.state.isVendorManagementEnabled && [
                      <PrivateRoute
                        key="1"
                        exact
                        path={'/vendorDetails/:id/profile'}
                        render={(props: any) => <VendorDetails {...props} tab={'profile'} />}
                      />,
                      <PrivateRoute
                        key="5"
                        exact
                        path={'/vendorDetails/:id/documents'}
                        render={(props: any) => <VendorDetails {...props} tab={'documents'} />}
                      />,
                      <PrivateRoute
                        key="6"
                        exact
                        path={'/vendorDetails/:id/hiring_companies'}
                        render={(props: any) => <VendorDetails {...props} tab={'hiring_companies'} />}
                      />,
                      <PrivateRoute
                        key="7"
                        exact
                        path={'/vendorDetails/:id/subscription'}
                        render={(props: any) => <VendorDetails {...props} tab={'subscription'} />}
                      />,
                    ]}
                  {this.state.hasVendorView && [
                    <PrivateRoute
                      key="8"
                      exact
                      path={'/vendorProfile/profile'}
                      render={(props: any) => <VendorDetails {...props} tab={'profile'} />}
                    />,
                    <PrivateRoute
                      key="11"
                      exact
                      path={'/vendorProfile/documents'}
                      render={(props: any) => <VendorDetails {...props} tab={'documents'} />}
                    />,
                  ]}
                  <Route exact path={'/workspaceRedirect'} render={(props: any) => <WorkspaceRedirect {...props} />} />
                </Switch>
              </Suspense>
            </Grid>
            <TnSnackBar
              open={this.state.showMessage}
              message={this.state.message}
              variant={this.state.variant}
              handleClose={this.handleClose}
            />
          </Grid>
        </Router>
      </MuiThemeProvider>
    );
  }
}

export default App;
